/**
 * Strip leaked rendering-tool tag emissions from the chat markdown buffer
 * (PLT-4968).
 *
 * The chat-only rendering tools (`present_headline_kpis`, `emit_callout`,
 * `suggest_followups`) must be invoked as structured tool calls — there is no
 * inline syntax for them. A drift mode observed in production has the model
 * emit a tool's full JSON payload inside an XML/HTML-like tag instead:
 *
 *   <headline_kpis>{"tiles":[{"label":"MATERIAL MATTERS","value":"24",…}]}</headline_kpis>
 *
 * `react-markdown` does not parse these tags (they aren't in the components
 * map), so it strips the tag but keeps the inner text — the raw JSON then
 * shows up as visible noise at the top of the answer. The server-side prompt
 * + syntax-leak detector steer the model away from this and log it, but as a
 * last line of defence we also strip the whole block client-side so the user
 * never sees a wall of JSON even when the model drifts.
 *
 * Pure-helper module (no JSX, no React imports) so the spec can pin the
 * regex without dragging the ESM-only `react-markdown` chain into mocha-unit.
 */

// The closed set of rendering-tool tag names the model has been seen to emit
// as text — the short `headline_kpis` form it actually produced plus the full
// tool names. Restricted to this set for the same false-positive reason as the
// server-side detector: a generic `<get_*>` match would hit legitimate prose.
const LEAKED_TOOL_TAGS = 'headline_kpis|present_headline_kpis|emit_callout|suggest_followups';

// Complete paired emission: opening tag, JSON payload, closing tag. Lazy inner
// match so multiple separate emissions in one message are each removed.
const PAIRED = new RegExp(`<(${LEAKED_TOOL_TAGS})\\b[^>]*>[\\s\\S]*?</\\1\\s*>`, 'gi');

// An emission still streaming in — opener (possibly not even closed with `>`
// yet) with no closing tag arrived. Anchored to end-of-string so a completed
// emission earlier in the message is handled by PAIRED, not here.
const TRAILING_UNCLOSED = new RegExp(`<(?:${LEAKED_TOOL_TAGS})\\b[\\s\\S]*$`, 'i');

export function stripLeakedToolTags(s: string): string {
  return s.replace(PAIRED, '').replace(TRAILING_UNCLOSED, '');
}
